import React, { useState, useEffect, useContext } from "react";
import { SessionContext } from "../../components/SessionContext";
import TopBar from "../../components/TopBar";
import { useNavigate } from "react-router-dom";
import supabase from "../../supabase";
import moment from "moment";
import { FaSearch, FaStar, FaRegStar, FaEdit, FaEye } from "react-icons/fa";
import { MdCheckCircle, MdCancel } from "react-icons/md";
import { toast } from "react-toastify";

const Ratings = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  // Open modal with selected rating data
  const handleEditClick = (rating) => {
    setEditData({
      ...rating,
      confirmDelete: false,
      saving: false,
      deleting: false,
    });
    setModalOpen(true);
  };

  // Close modal
  const handleModalClose = () => {
    setModalOpen(false);
    setEditData(null);
  };

  // Handle update (edit) rating
  const handleUpdate = async (e) => {
    e.preventDefault();
    setEditData((prev) => ({ ...prev, saving: true }));
    const { id, username, rating, comment } = editData;
    const { data, error } = await supabase
      .from("ratings")
      .update({ username, rating, comment })
      .eq("id", id)
      .select();
    if (error) {
      toast.error("Failed to update rating: " + error.message);
      setEditData((prev) => ({ ...prev, saving: false }));
    } else {
      toast.success("Rating updated successfully");
      setRatings((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, username, rating, comment } : r,
        ),
      );
      handleModalClose();
    }
  };

  // Handle delete rating
  const handleDelete = async () => {
    setEditData((prev) => ({ ...prev, deleting: true }));
    const { id } = editData;
    const { error } = await supabase.from("ratings").delete().eq("id", id);
    if (error) {
      toast.error("Failed to delete rating: " + error.message);
      setEditData((prev) => ({ ...prev, deleting: false }));
    } else {
      toast.success("Rating deleted successfully");
      setRatings((prev) => prev.filter((r) => r.id !== id));
      handleModalClose();
    }
  };
  const { session } = useContext(SessionContext);
  const navigate = useNavigate();
  const [ratings, setRatings] = useState([]);
  const [status, setStatus] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    const fetchRatings = async () => {
      setLoading(true);
      let query = supabase
        .from("ratings")
        .select("*, products(id, name, images), username")
        .order("created_at", { ascending: false });

      if (status === "pending") {
        query = query.eq("approved", false);
      } else if (status === "approved") {
        query = query.eq("approved", true);
      }
      // For "all" status, no additional filter is needed

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching ratings: ", error);
        toast.error("Error fetching ratings");
      } else {
        setRatings(data || []);
      }
      setLoading(false);
    };

    fetchRatings();
    const intervalId = setInterval(fetchRatings, 300000); // Refresh every 5 minutes
    return () => clearInterval(intervalId);
  }, [status]);

  const filteredRatings = ratings.filter((rating) => {
    const productName = rating.products?.name || "";
    const comment = rating.comment || "";
    const ratingValue = rating.rating ? rating.rating.toString() : "";

    return (
      productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ratingValue.includes(searchTerm)
    );
  });

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        i <= rating ? (
          <FaStar key={i} className="text-yellow-500" />
        ) : (
          <FaRegStar key={i} className="text-gray-300" />
        ),
      );
    }
    return stars;
  };

  const handleApprove = async (ratingId, e) => {
    e.stopPropagation();
    console.log("[APPROVE] Starting approval process for rating ID:", ratingId);
    setActionLoading(ratingId);

    console.log("[APPROVE] Sending update request to Supabase...");
    const { data, error } = await supabase
      .from("ratings")
      .update({ approved: true })
      .eq("id", ratingId)
      .select();

    console.log("[APPROVE] Supabase response:", { data, error });

    if (error) {
      console.error("[APPROVE] Error approving rating:", error);
      console.error("[APPROVE] Error details:", JSON.stringify(error, null, 2));
      toast.error(`Error approving rating: ${error.message}`);
    } else {
      console.log(
        "[APPROVE] Successfully approved rating. Updated data:",
        data,
      );
      toast.success("Rating approved successfully");
      setRatings((prev) => prev.filter((rating) => rating.id !== ratingId));
    }
    setActionLoading(null);
  };

  const handleReject = async (ratingId, e) => {
    e.stopPropagation();

    const ConfirmRejectToast = ({ closeToast }) => (
      <div>
        <p className="font-semibold mb-2">
          Are you sure you want to reject this rating?
        </p>
        <p className="text-sm text-gray-600 mb-3">
          This will permanently delete the rating from the database.
        </p>
        <div className="flex justify-center gap-3">
          <button
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 font-semibold"
            onClick={async () => {
              console.log(
                "[REJECT] Starting deletion process for rating ID:",
                ratingId,
              );
              setActionLoading(ratingId);
              closeToast();

              console.log("[REJECT] Sending delete request to Supabase...");
              const { data, error } = await supabase
                .from("ratings")
                .delete()
                .eq("id", ratingId)
                .select();

              console.log("[REJECT] Supabase response:", { data, error });

              if (error) {
                console.error("[REJECT] Error rejecting rating:", error);
                console.error(
                  "[REJECT] Error details:",
                  JSON.stringify(error, null, 2),
                );
                toast.error(`Error rejecting rating: ${error.message}`);
              } else {
                console.log(
                  "[REJECT] Successfully deleted rating. Deleted data:",
                  data,
                );
                toast.success("Rating rejected and deleted");
                setRatings((prev) =>
                  prev.filter((rating) => rating.id !== ratingId),
                );
              }
              setActionLoading(null);
            }}
          >
            Yes, Delete
          </button>
          <button
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 font-semibold"
            onClick={closeToast}
          >
            Cancel
          </button>
        </div>
      </div>
    );

    toast.warning(<ConfirmRejectToast />, {
      position: "top-center",
      autoClose: false,
      closeOnClick: false,
      draggable: false,
    });
  };

  const handleCardClick = (productId) => {
    if (productId) {
      window.open(
        `https://taqwafashionstore.com/product/${productId}`,
        "_blank",
      );
    }
  };

  return (
    <div
      className={`page h-screen overflow-y-scroll pb-10 ${modalOpen ? "pointer-events-none" : ""}`}
    >
      <TopBar avatarInfo={session?.user.user_metadata} />

      <div className="flex justify-center my-4 flex-wrap">
        <button
          className={`mx-2 py-2 px-4 ${
            status === "pending"
              ? "bg-blue-500 text-white"
              : "bg-white text-blue-500"
          } rounded shadow-md`}
          onClick={() => setStatus("pending")}
        >
          Pending Approval
        </button>
        <button
          className={`mx-2 py-2 px-4 ${
            status === "approved"
              ? "bg-blue-500 text-white"
              : "bg-white text-blue-500"
          } rounded shadow-md`}
          onClick={() => setStatus("approved")}
        >
          Approved
        </button>
        <button
          className={`mx-2 py-2 px-4 ${
            status === "all"
              ? "bg-blue-500 text-white"
              : "bg-white text-blue-500"
          } rounded shadow-md`}
          onClick={() => setStatus("all")}
        >
          All Ratings
        </button>
      </div>

      <div className="flex justify-center my-4 px-4">
        <div className="relative w-full max-w-lg">
          <input
            type="text"
            placeholder="Search by product name, comment, or rating"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border rounded w-full pl-10"
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-600 my-10">
          <p>Loading ratings...</p>
        </div>
      ) : filteredRatings.length === 0 ? (
        <div className="text-center text-gray-600 my-10">
          <p className="text-xl">
            {status === "pending"
              ? "No pending ratings"
              : status === "approved"
                ? "No approved ratings yet"
                : "No ratings found"}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 p-4">
          {filteredRatings.map((rating) => (
            <div
              key={rating.id}
              className="flex flex-col sm:flex-row border rounded-lg shadow-md bg-white hover:shadow-xl transition-all cursor-pointer overflow-hidden"
              onClick={() => handleEditClick(rating)}
            >
              {/* Left Section - Product Info (Small) */}
              <div className="flex sm:flex-col items-center sm:w-32 p-3 bg-gray-50 border-b sm:border-b-0 sm:border-r border-gray-200">
                {rating.products &&
                  rating.products.images &&
                  rating.products.images[0] && (
                    <img
                      src={rating.products.images[0]}
                      alt={rating.products.name}
                      className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md"
                    />
                  )}
                {rating.products && (
                  <div className="ml-3 sm:ml-0 sm:mt-2 text-center">
                    <p className="text-xs font-semibold text-gray-700 line-clamp-2">
                      {rating.products.name}
                    </p>
                  </div>
                )}
              </div>

              {/* Right Section - Rating & Comment Details (Prominent) */}
              <div className="flex-1 p-4">
                <div className="flex flex-col h-full">
                  {/* Username */}
                  {rating.username && (
                    <div className="mb-1">
                      <span className="text-xs font-semibold text-blue-700">
                        {rating.username}
                      </span>
                    </div>
                  )}
                  {/* Rating Stars and Value */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {renderStars(rating.rating)}
                      </div>
                      <span className="text-lg font-bold text-gray-800">
                        {rating.rating}/5
                      </span>
                    </div>

                    {/* Status Badge */}
                    <span
                      className={`text-xs font-bold px-3 py-1 rounded-full ${
                        rating.approved
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {rating.approved ? "Approved" : "Pending"}
                    </span>
                  </div>

                  {/* Comment Section */}
                  {rating.comment && (
                    <div className="mb-3 flex-1 relative">
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                        <p className="text-sm text-gray-800 italic leading-relaxed">
                          "{rating.comment}"
                        </p>
                        {rating.approved && (
                          <div className="absolute top-2 right-2 flex gap-2">
                            <button
                              className="flex items-center gap-1 px-3 py-2 text-sm bg-yellow-200 hover:bg-yellow-300 rounded text-yellow-900 font-semibold shadow"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(rating);
                              }}
                            >
                              <FaEdit size={14} />
                              Edit
                            </button>
                            <button
                              className="flex items-center gap-1 px-3 py-2 text-sm bg-blue-200 hover:bg-blue-300 rounded text-blue-900 font-semibold shadow"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCardClick(rating.products?.id);
                              }}
                            >
                              <FaEye size={14} />
                              View
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Modal for editing comment, rating, and username */}
                  {modalOpen && editData && (
                    <div
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 pointer-events-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModalClose();
                      }}
                    >
                      <div
                        className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl"
                          onClick={handleModalClose}
                        >
                          &times;
                        </button>
                        <h2 className="text-lg font-bold mb-4">Edit Rating</h2>
                        <form onSubmit={handleUpdate}>
                          <div className="mb-3">
                            <label className="block text-sm font-semibold mb-1">
                              Username
                            </label>
                            <input
                              type="text"
                              className="w-full border rounded px-3 py-2"
                              value={editData.username || ""}
                              onChange={(e) =>
                                setEditData({
                                  ...editData,
                                  username: e.target.value,
                                })
                              }
                              disabled={editData.saving || editData.deleting}
                            />
                          </div>
                          <div className="mb-3">
                            <label className="block text-sm font-semibold mb-1">
                              Rating
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="5"
                              className="w-full border rounded px-3 py-2"
                              value={editData.rating}
                              onChange={(e) =>
                                setEditData({
                                  ...editData,
                                  rating: Number(e.target.value),
                                })
                              }
                              disabled={editData.saving || editData.deleting}
                            />
                          </div>
                          <div className="mb-3">
                            <label className="block text-sm font-semibold mb-1">
                              Comment
                            </label>
                            <textarea
                              className="w-full border rounded px-3 py-2"
                              rows={3}
                              value={editData.comment || ""}
                              onChange={(e) =>
                                setEditData({
                                  ...editData,
                                  comment: e.target.value,
                                })
                              }
                              disabled={editData.saving || editData.deleting}
                            />
                          </div>
                          <div className="flex justify-between items-center mt-6">
                            <button
                              type="button"
                              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 font-semibold"
                              onClick={() =>
                                setEditData({
                                  ...editData,
                                  confirmDelete: true,
                                })
                              }
                              disabled={editData.saving || editData.deleting}
                            >
                              Delete
                            </button>
                            <button
                              type="submit"
                              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 font-semibold"
                              disabled={editData.saving || editData.deleting}
                            >
                              {editData.saving ? "Saving..." : "Save Changes"}
                            </button>
                          </div>
                          {/* Delete confirmation */}
                          {editData.confirmDelete && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-300 rounded">
                              <p className="text-red-700 mb-2">
                                Are you sure you want to delete this rating?
                              </p>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 font-semibold"
                                  onClick={handleDelete}
                                  disabled={editData.deleting}
                                >
                                  {editData.deleting
                                    ? "Deleting..."
                                    : "Yes, Delete"}
                                </button>
                                <button
                                  type="button"
                                  className="bg-gray-300 text-gray-800 px-3 py-1 rounded hover:bg-gray-400 font-semibold"
                                  onClick={() =>
                                    setEditData({
                                      ...editData,
                                      confirmDelete: false,
                                    })
                                  }
                                  disabled={editData.deleting}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </form>
                      </div>
                    </div>
                  )}

                  {/* Bottom Section - Timestamp and Actions */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-auto">
                    <div className="text-xs text-gray-500">
                      {moment(rating.created_at).format("MMM Do YYYY, h:mm A")}
                    </div>

                    {/* Action Buttons for Pending Ratings */}
                    {!rating.approved && status === "pending" && (
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => handleApprove(rating.id, e)}
                          disabled={actionLoading === rating.id}
                          className="flex items-center gap-1 px-4 py-2 bg-green-500 text-white text-sm font-semibold rounded hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdCheckCircle size={16} />
                          {actionLoading === rating.id
                            ? "Approving..."
                            : "Approve"}
                        </button>
                        <button
                          onClick={(e) => handleReject(rating.id, e)}
                          disabled={actionLoading === rating.id}
                          className="flex items-center gap-1 px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdCancel size={16} />
                          {actionLoading === rating.id
                            ? "Rejecting..."
                            : "Reject"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Ratings;
